import { useEffect, useState, useCallback, useRef } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import api from '@services/api';

export const useAttachmentGallery = ({
  entityType,
  entityId,
  context,
  maxItems,
  thumbnailSize,
  onAttachmentDelete,
  onAttachmentUpdate
}) => {
  const { t } = useLanguage();
  const notify = useNotification();
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const debounceTimeoutRef = useRef(null);

  const fetchAttachments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the proper API method
      const response = await api.attachments.getEntityAttachments(entityType, entityId, context);

      let attachmentData = response.data;

      // Vérifier s'il y a des conflits d'ordre (plusieurs attachments avec le même display_order)
      const orders = attachmentData.map(att => att.display_order);
      const hasDuplicates = orders.length !== new Set(orders).size;

      if (hasDuplicates) {
        // Corriger automatiquement les ordres en conflit
        try {
          const fixResponse = await api.attachments.fixOrders(entityType, entityId, context);
          attachmentData = fixResponse.data;
        } catch (fixError) {
          console.warn('Could not auto-fix duplicate orders:', fixError);
          // Continuer avec les données existantes même si la correction échoue
        }
      }

      // Limit items if maxItems is specified
      if (maxItems && Array.isArray(attachmentData)) {
        attachmentData = attachmentData.slice(0, maxItems);
      }

      setAttachments(attachmentData);
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
      setError(t('attachments.fetchError'));

      // Don't show error notification for 404 - just means no attachments
      if (error.response?.status !== 404) {
        notify.error(t('attachments.fetchError'));
      }
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, context, maxItems, t, notify]);

  const handleDelete = async (attachmentId) => {
    if (!window.confirm(t('attachments.confirmDelete'))) {
      return;
    }

    try {
      await api.delete(`/attachments/${attachmentId}?delete_file=true`);

      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      onAttachmentDelete?.(attachmentId);

      notify.success(t('attachments.deleteSuccess'));
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      notify.error(t('attachments.deleteError'));
    }
  };

  const handleOrderUpdate = async (attachmentId, direction) => {
    try {
      // Utiliser la nouvelle API de réordonnancement
      const response = await api.attachments.reorder(attachmentId, direction);

      // Mettre à jour directement avec la réponse du serveur qui contient la liste triée
      setAttachments(response.data);

      // Force un nouveau rendu pour recalculer les indices canMoveUp/canMoveDown
      // Utiliser setTimeout pour s'assurer que le state est bien mis à jour
      setTimeout(() => {
        setRefreshKey(prev => prev + 1);
      }, 0);

      onAttachmentUpdate?.(attachmentId);

      notify.success(t('attachments.orderUpdateSuccess'));
    } catch (error) {
      console.error('Failed to update attachment order:', error);
      notify.error(t('attachments.orderUpdateError'));
    }
  };

  const handleEditComplete = async (result) => {
    try {
      // Refresh attachments to show the updated/new attachment
      await fetchAttachments();

      // Force re-render to show updated images
      setRefreshKey(prev => prev + 1);

      onAttachmentUpdate?.();

      // Show appropriate success notification based on the type of edit
      if (result?.new_attachment) {
        notify.success(t('attachments.aiEditSuccess'));
      }
    } catch (error) {
      console.error('Failed to refresh attachments after edit:', error);
      // Still refresh to be safe
      await fetchAttachments();
      setRefreshKey(prev => prev + 1);
    }
  };

  useEffect(() => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce the fetchAttachments call
    debounceTimeoutRef.current = setTimeout(() => {
      fetchAttachments();
    }, 100); // 100ms debounce delay

    // Cleanup function
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [entityType, entityId, context, thumbnailSize]);

  return {
    attachments,
    loading,
    error,
    refreshKey,
    handleDelete,
    handleOrderUpdate,
    handleEditComplete,
    fetchAttachments
  };
};